import React, { FC, useRef, useMemo } from 'react';
import { useCustomThemeDraftDispatch, useCustomThemeDraftState } from '@olo-web/client-state';
import {
  useAllMerchantImages,
  useMerchantTheme,
  IPreviousImageResponse,
} from '@domain/merchants/queries';
import { useDeleteMerchantImage } from '@domain/merchants/mutations/useDeleteMerchantImage';
import { useError } from '@olo-web/utils/common/hooks';
import {
  Text,
  Wrap,
  Image,
  VStack,
  Box,
  ButtonGroup,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Skeleton,
} from '@chakra-ui/react';
import { ECustomThemeViews } from '@olo-web/types/enums';

type T = {
  image: IPreviousImageResponse;
};

const ImageWithButtons: FC<T> = ({ image }) => {
  const callError = useError();
  const { view } = useCustomThemeDraftState();
  const themeDispatch = useCustomThemeDraftDispatch();
  const { mutate, isLoading } = useDeleteMerchantImage({
    onError: (error) => callError({ error, toastError: true }),
  });
  const isLogo = view === ECustomThemeViews.LOGO;
  const isFavicon = view === ECustomThemeViews.FAVICON;
  const initFocusRef = useRef(null);

  const handlePreview = () => {
    const actionType = isFavicon
      ? 'UPDATE_FAVICON_URL'
      : isLogo
      ? 'UPDATE_LOGO_URL'
      : 'UPDATE_BACKGROUND_URL';
    themeDispatch({
      type: actionType,
      payload: image.url,
    });
  };

  const handleDelete = () => {
    mutate(image.fullPath);
  };

  return (
    <Box w="100px" pos="relative">
      <Popover initialFocusRef={initFocusRef}>
        <PopoverTrigger>
          <Button variant="ghost" h={100} w={100} p={2}>
            <Image borderRadius="md" key={image?.name} src={image?.url} alt={image?.name} />
          </Button>
        </PopoverTrigger>
        <PopoverContent w="auto">
          <PopoverArrow />
          <PopoverBody justifyContent="center" w="100%">
            <ButtonGroup isAttached size="sm">
              <Button variant="outline" colorScheme="primary" onClick={handlePreview}>
                Preview
              </Button>
              <Button
                variant="outline"
                colorScheme="danger"
                onClick={handleDelete}
                isLoading={isLoading}
              >
                Delete
              </Button>
            </ButtonGroup>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export const PreviousImages = () => {
  const { view } = useCustomThemeDraftState();
  const { data: theme } = useMerchantTheme();
  const { data: images, isLoading } = useAllMerchantImages(view as ECustomThemeViews);

  const prevImages = useMemo(() => {
    if (!images?.length) return [];
    const isLogo = view === ECustomThemeViews.LOGO;
    const isFavicon = view === ECustomThemeViews.FAVICON;
    return images?.filter((img) => {
      const currUrl = isFavicon
        ? theme?.faviconUrl
        : isLogo
        ? theme?.logoUrl
        : theme?.backgroundUrl;
      return img.url !== currUrl;
    });
  }, [theme, images, view]);

  if (!isLoading && !prevImages?.length) return null;

  return (
    <VStack w="100%" spacing={6}>
      <Text textStyle="subtitle2">Previous images</Text>
      <Wrap spacing={6}>
        {isLoading
          ? new Array(3)
              .fill(true)
              .map((_, idx) => (
                <Skeleton
                  key={`loading-image={${idx - 1}}`}
                  h="100px"
                  w="100px"
                  borderRadius="md"
                />
              ))
          : prevImages?.map((image) => <ImageWithButtons key={image?.name} image={image} />)}
      </Wrap>
    </VStack>
  );
};
